import type { AuctisyncSupabase } from './client.js';
import { buildBidderDashboard, fetchMyBidLots, type DashboardLot } from './dashboard.js';
import { fetchMyWatchedLotIds } from './watchlist.js';

/**
 * Where this bidder stands in EACH SALE — the numbers behind the design's
 * `YoursLine` on a sale card ("1 winning · 3 bidding") and `SaleStat` on the
 * sale page (Bidding / Winning / Watching).
 *
 * 🔴 NOT A SECOND SET OF RULES. The dashboard already decides what "winning",
 * "outbid", "watching" and "won" mean (`buildBidderDashboard`); this module
 * only GROUPS that answer by sale. The website's sales page had its own inline
 * copy of the winning/won rule and the app had none — which is how the web came
 * to show "Winning 2 in this sale" while the app's card said nothing. Both now
 * read from here.
 *
 * WORDING. The sale layer says "bidding" where the dashboard says "outbid":
 * same lots (bid on, open, not leading), calmer word. On a sale card the
 * question is "am I in this sale?", not "am I losing?" — the design chose the
 * word, and the lot page still says outbid where it matters.
 */

export interface SaleStanding {
  /** Open lots in this sale where the bidder currently leads. */
  winning: number;
  /** Open lots in this sale the bidder has bid on and does NOT lead. */
  bidding: number;
  /** Lots in this sale on the bidder's watchlist, whatever their state. */
  watching: number;
  /** Sold lots in this sale the bidder won. */
  won: number;
}

export const EMPTY_SALE_STANDING: SaleStanding = { winning: 0, bidding: 0, watching: 0, won: 0 };

/** What the grouping needs: the dashboard's fields plus which sale a lot is in. */
export interface SaleStandingLot extends DashboardLot {
  auction_id: string;
}

/**
 * The columns `fetchMySaleStandings` reads. Nothing a rival could learn from —
 * and no bidder ids: a signed-in bidder cannot read them (2026-09-23), and
 * naming them failed this whole read. Standing comes from `fetchMyBidLots`.
 */
export const SALE_STANDING_COLUMNS = 'id, status, ends_at, auction_id';

/**
 * Group the dashboard's answer by sale. Pure.
 *
 * A sale appears in the result only when the bidder has SOMETHING in it — a
 * bid, a watched lot or a win. Callers treat a missing key as
 * `EMPTY_SALE_STANDING`, so the card for a sale the bidder has never touched
 * shows nothing rather than a row of zeros.
 */
export function groupStandingsBySale<T extends SaleStandingLot>(input: {
  bidLots: readonly T[];
  watchedLots: readonly T[];
  nowMs: number;
}): Map<string, SaleStanding> {
  const dash = buildBidderDashboard<T>({
    bidLots: [...input.bidLots],
    watchedLots: [...input.watchedLots],
    nowMs: input.nowMs,
  });

  const out = new Map<string, SaleStanding>();
  const bump = (auctionId: string, key: keyof SaleStanding) => {
    const entry = out.get(auctionId) ?? { ...EMPTY_SALE_STANDING };
    entry[key] += 1;
    out.set(auctionId, entry);
  };
  for (const lot of dash.winning) bump(lot.auction_id, 'winning');
  for (const lot of dash.outbid) bump(lot.auction_id, 'bidding');
  for (const lot of dash.watching) bump(lot.auction_id, 'watching');
  for (const lot of dash.won) bump(lot.auction_id, 'won');
  return out;
}

/**
 * Fetch and group, for a signed-in bidder.
 *
 * Two reads: the bidder's own bids (RLS confines `bids` to the caller) joined
 * to the lot fields the rules need, and the watched lots by id. Never throws —
 * a sales list that cannot say where the bidder stands still lists the sales;
 * the caller gets an empty map and the error, and shows the cards without the
 * line rather than no cards.
 */
export async function fetchMySaleStandings(
  supabase: AuctisyncSupabase,
  userId: string,
  nowMs: number,
): Promise<{ standings: Map<string, SaleStanding>; error: string | null }> {
  try {
    const [bid, watchedIds] = await Promise.all([
      fetchMyBidLots<SaleStandingLot>(supabase, userId, SALE_STANDING_COLUMNS),
      fetchMyWatchedLotIds(supabase, userId),
    ]);
    if (bid.error !== null) return { standings: new Map(), error: bid.error };

    let watchedLots: SaleStandingLot[] = [];
    if (watchedIds.length > 0) {
      const { data, error } = await supabase
        .from('lots')
        .select(SALE_STANDING_COLUMNS)
        .in('id', watchedIds);
      if (error) return { standings: new Map(), error: error.message };
      watchedLots = (data ?? []) as unknown as SaleStandingLot[];
    }

    return {
      standings: groupStandingsBySale({ bidLots: bid.lots, watchedLots, nowMs }),
      error: null,
    };
  } catch (e) {
    return { standings: new Map(), error: e instanceof Error ? e.message : 'Could not load' };
  }
}

/** One piece of the sale card's line. `winning` is the only one drawn as a tag. */
export interface YoursLinePart {
  kind: 'winning' | 'bidding' | 'watching';
  text: string;
}

/**
 * The design's `YoursLine` rule (`app_ios/bidder-auctions.jsx`):
 *
 *   - "N winning" as a winning tag, when N > 0
 *   - "N bidding" in muted text, when N > 0
 *   - "N watching" ONLY when neither of the above — a bidder who is bidding
 *     does not need telling they are also watching
 *   - nothing at all when the bidder has nothing in the sale
 *
 * Shared so the phone's card and the website's card say the same thing about
 * the same sale.
 */
export function yoursLineParts(standing: SaleStanding | null | undefined): YoursLinePart[] {
  if (!standing) return [];
  const parts: YoursLinePart[] = [];
  if (standing.winning > 0) {
    parts.push({ kind: 'winning', text: `${String(standing.winning)} winning` });
  }
  if (standing.bidding > 0) {
    parts.push({ kind: 'bidding', text: `${String(standing.bidding)} bidding` });
  }
  if (parts.length === 0 && standing.watching > 0) {
    parts.push({ kind: 'watching', text: `${String(standing.watching)} watching` });
  }
  return parts;
}
